import { Box, Button, Checkbox } from '@mui/material';
import { UserData } from '../../../data_layer/user/UserData';
import { FunctionComponent, useState, useEffect } from 'react';
import { ErrorPageNoAdmin } from '../../error/ErrorPageNoAdmin';
import { ApiObjBaseData } from '../../../api/object/base_data/ApiObjBaseData';
import { ApiObjAdminBaseData } from '../../../api/object/admin/ApiObjAdminBaseData';
import { api_admin_user_get_export_fields } from '../../../api/endpoint/admin/user/export_field/api_admin_user_get_export_fields';
import { api_admin_user_save_export_fields } from '../../../api/endpoint/admin/user/export_field/api_admin_user_save_export_fields';
import { ApiObjExportField } from '../../../api/object/export/ApiObjExportField';

type Props = {
    baseData: ApiObjBaseData,
    userData: UserData,
    adminBaseData: ApiObjAdminBaseData,
    otherUserId: number,
}

export const AdminUserTabExportFields: FunctionComponent <Props> = ({
    baseData,
    userData,
    adminBaseData,
    otherUserId
}) => {

    if (!userData.isLoggedInAndAdmin()) {
        return <ErrorPageNoAdmin />;
    }

    const [mapUserExportFields, setMapUserExportFields] = useState <Map<number, number>> (new Map());
    const [isLoading, setIsLoading] = useState <boolean> (true);
    const [isSaving, setIsSaving] = useState <boolean> (false);
    const isActive = !isLoading && !isSaving;

    // ======================================================================
    // === Setup
    // ======================================================================

    useEffect(() => {

        setIsLoading(true);
        api_admin_user_get_export_fields(otherUserId)
            .then((result) => {
                const newMap = new Map();
                for (const item of result) {
                    newMap.set(item, item);
                }
                setMapUserExportFields(newMap);
                setIsLoading(false);
            });

    }, [otherUserId]);

    // ======================================================================
    // === Actions
    // ======================================================================
  
    const actionSaveExportFields = () => {

        setIsSaving(true);

        const exportFieldIds : Array<number> = [];
        mapUserExportFields.forEach((value: number, key: number) => {
            exportFieldIds.push(key);
        });

        api_admin_user_save_export_fields(otherUserId, exportFieldIds)
            .then((result) => {
                const newMap = new Map();
                for (const item of result) {
                    newMap.set(item, item);
                }
                setMapUserExportFields(newMap);
                setIsSaving(false);

            }).catch((err) => {
                console.error("Could not save export fields");
                console.error(err);
                // Skip to set isSaving to fails to make error obvious.
            });
    }

    const actionOnCheckboxChange = (exportFieldId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const newMap = new Map(mapUserExportFields);
        if (isChecked) {
            newMap.set(exportFieldId, exportFieldId);
        } else {
            newMap.delete(exportFieldId);
        }
        setMapUserExportFields(newMap);
    }
    
    // ======================================================================
    // === Render
    // ======================================================================

    const renderList = () : JSX.Element => {

        const elements : Array<JSX.Element> = [];
        for (const item of adminBaseData.export_fields) {
            if (item.is_premium) {
                elements.push(renderItem(item));
            }
        }

        return (
            <Box>
                {elements}
            </Box>
        )
    }

    const renderItem = (exportField: ApiObjExportField) : JSX.Element => {

        const isSelected = mapUserExportFields.has(exportField.id);

        return (
            <Box
                key={exportField.id}
                display={'flex'}
                flexDirection={'row'}
                alignContent={'center'}>
                    <Box>
                        <Checkbox
                            checked={isSelected}
                            disabled={!isActive}
                            color={'secondary'}
                            onClick={(event) => { event.stopPropagation(); }}
                            onChange={(event) => { actionOnCheckboxChange(exportField.id, event) }}/>
                    </Box>
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                        {exportField.name}
                    </Box>
            </Box>
        );
    }

    if (isLoading) {
        return (<Box padding={'14px'}>Laddar...</Box>);
    }

    return (
        <Box padding={'14px'} display={'flex'} flexDirection={'column'}>
            {renderList()}
            <Box paddingTop={'15px'}>
                <Button variant='contained' color='secondary' onClick={actionSaveExportFields} disabled={!isActive}>
                    Spara
                </Button>
            </Box>
        </Box>
    );
}
